/**
 * Registre des routes v10 par zone (paquet v0-p00-ouverture ; plan de réalisation v10 § 3.1-4, décision D6).
 *
 * `App.tsx` est GELÉ après ce paquet : aucune vague n'y écrit. Une vague déclare ses routes dans
 * `src/v10/routes/<zone>.tsx` (ex. `v1.tsx`, `v8a.tsx`, `v10c.tsx`), qui exporte
 * `export const fragment: FragmentRoutes = { zone: '<zone>', routes: [...] }` ; le registre le découvre seul
 * (`import.meta.glob`). Un fragment n'importe du registre que des TYPES (`import type`) : un import de valeur
 * créerait un cycle au chargement.
 *
 * Les routes existantes restent écrites en JSX dans `App.tsx` (des outils du dépôt y lisent `<Route path="…">`) ;
 * elles forment le fragment « socle », déclaré ici en données (`ROUTES_SOCLE`, comparé à App.tsx par le test).
 * Les comparaisons utilisent des motifs normalisés : les noms des paramètres sont effacés, leur caractère
 * optionnel est conservé, les barres multiples sont réduites et la barre finale est retirée (sauf `/`) ;
 * les diagnostics et le routeur gardent les chemins écrits. Un fragment remplace
 * explicitement une route du socle (`remplace: true`) ; une déclaration en problème n'est jamais rendue.
 * Les extensions `publiques` sont rendues sans session, avant les écrans historiques, avec correspondance
 * exacte et montées comme composants sous `<Routes>` pour fournir le contexte de route à leurs hooks.
 * Elles n'acceptent ni segment optionnel ni splat, et aucun chevauchement avec un chemin privé : les segments
 * optionnels privés sont développés, les paramètres acceptent tout segment, le splat final tout suffixe.
 * `coquille` est LA coquille de l'application et `connexion` son écran de connexion (sans props) : chaque extension
 * exige un fournisseur unique ; sans lui, `App.tsx` rend un état d'erreur nommé, jamais un repli. Les composants objets
 * React sont acceptés, les instances refusées. Les routes de fragments précèdent le socle pour permettre `remplace`.
 * Retrait de l'historique, paquet R3 (décision D2 du 02/11/2026) : le drapeau `navigationV10`, l'AppShell et l'écran
 * Login historiques sont retirés, ces extensions sont actives sans condition. L'ancienne extension `amorce` (point
 * d'amorçage du drapeau par organisation) est retirée avec lui : un fragment qui la déclare est refusé.
 *
 * Règle de chargement (`import.meta.glob` est `eager`) : un fragment est importé dans le paquet principal. Il ne déclare donc que des composants chargés à la demande (`lazy`) et des données, sans code exécuté au
 * chargement du module (ni appel réseau, ni effet, ni calcul lourd) : une erreur au chargement casserait toute
 * l'application, un fragment lourd alourdirait le premier chargement de tous les utilisateurs.
 */
import { createElement, type ComponentType, type JSX, type ReactNode } from 'react'
import { matchRoutes, Route, Routes } from 'react-router-dom'
import type { Session } from '../../auth/session'
import { CHEMINS_SANS_ECRAN } from '../../lib/nav'

export type Traduire = (cle: string, repli?: string, variables?: Record<string, string | number>) => string
export interface ContexteRoutes { session: Session; superAdmin: boolean; t: Traduire }
export interface DeclarationRoute {
  chemin: string
  element: (ctx: ContexteRoutes) => JSX.Element
  remplace?: true
}
export interface PropsCoquille { session: Session; children: ReactNode }
export interface RoutePublique { chemin: string; ecran: ComponentType }
export interface FragmentRoutes {
  zone: string
  routes: readonly DeclarationRoute[]
  publiques?: readonly RoutePublique[]
  coquille?: ComponentType<PropsCoquille>
  connexion?: ComponentType
}
export interface RouteSocle { chemin: string; redirection: boolean }
export const ZONE_SOCLE = 'socle'

/** Inventaire statique ; le JSX historique reste dans App.tsx. */
export const ROUTES_SOCLE: readonly RouteSocle[] = [
  { chemin: '/', redirection: false },
  { chemin: '/tableau-de-bord', redirection: false },
  { chemin: '/administration', redirection: false },
  { chemin: '/demandes', redirection: false },
  { chemin: '/demandes/:id', redirection: false },
  { chemin: '/demandes/:id/echanges', redirection: false },
  { chemin: '/ideas', redirection: false },
  { chemin: '/ingestion', redirection: false },
  { chemin: '/chat', redirection: false },
  { chemin: '/workflow', redirection: false },
  { chemin: '/compare', redirection: false },
  { chemin: '/projet', redirection: false },
  { chemin: '/projet/membres', redirection: false },
  { chemin: '/backlog', redirection: true },
  { chemin: '/items/:id', redirection: false },
  { chemin: '/tree', redirection: true },
  { chemin: '/graph', redirection: true },
  { chemin: '/sprints', redirection: true },
  { chemin: '/sprints/:sprintId', redirection: true },
  { chemin: '/roadmap', redirection: true },
  { chemin: '/specifications', redirection: false },
  { chemin: '/editor', redirection: false },
  { chemin: '/specifications/modifications', redirection: false },
  { chemin: '/specifications/modifications/:id', redirection: false },
  { chemin: '/projet/classement', redirection: false },
  { chemin: '/projet/classement/:nature/:id', redirection: false },
  { chemin: '/equipes/:teamId', redirection: false },
  { chemin: '/dependencies', redirection: false },
  { chemin: '/traceability', redirection: false },
  { chemin: '/tests', redirection: false },
  { chemin: '/anomalies', redirection: false },
  { chemin: '/tests/cas/:caseId', redirection: false },
  { chemin: '/tests/campagnes/:campaignId/executions/:executionId', redirection: false },
  { chemin: '/tests/campagnes/:campaignId/executer', redirection: false },
  { chemin: '/tests/plans/:planId', redirection: false },
  { chemin: '/recette', redirection: false },
  { chemin: '/qualite', redirection: false },
  { chemin: '/design', redirection: false },
  { chemin: '/design/comparaisons/:id', redirection: false },
  { chemin: '/design/tests-usage/:id', redirection: false },
  { chemin: '/apercu', redirection: false },
  { chemin: '/organisation', redirection: false },
  { chemin: '/decisions', redirection: false },
  { chemin: '/technologies', redirection: false },
  { chemin: '/architecture', redirection: false },
  { chemin: '/architecture/versions/:releaseId/composant', redirection: false },
  { chemin: '/architecture/versions/:releaseId/dependance', redirection: false },
  { chemin: '/technologies/changements', redirection: false },
  { chemin: '/technologies/changements/:changeId', redirection: false },
  { chemin: '/documentation', redirection: false },
  { chemin: '/approvals', redirection: false },
  { chemin: '/approvals/:id', redirection: false },
  { chemin: '/hitl-cockpit', redirection: false },
  { chemin: '/attention', redirection: false },
  { chemin: '/mon-travail', redirection: false },
  { chemin: '/invitations', redirection: false },
  { chemin: '/missions', redirection: false },
  { chemin: '/supervision', redirection: false },
  { chemin: '/artifacts', redirection: false },
  { chemin: '/history', redirection: false },
  { chemin: '/notifications', redirection: false },
  { chemin: '/quotas', redirection: false },
  { chemin: '/scorecard', redirection: false },
  { chemin: '/audit-roadmap', redirection: false },
  { chemin: '/timeline', redirection: false },
  { chemin: '/pipelines', redirection: false },
  { chemin: '/runops', redirection: false },
  { chemin: '/runops/deploiements/:deploymentId', redirection: false },
  { chemin: '/runops/environnements/:environmentId', redirection: false },
  { chemin: '/versions', redirection: false },
  { chemin: '/versions/:releaseId/documents/:kind', redirection: false },
  { chemin: '/publication', redirection: false },
  { chemin: '/delivery', redirection: false },
  { chemin: '/exploitation', redirection: false },
  { chemin: '/exploitation/supervision', redirection: false },
  { chemin: '/exploitation/objectifs/:id', redirection: false },
  { chemin: '/incidents', redirection: false },
  { chemin: '/incidents/:id', redirection: false },
  { chemin: '/calendrier', redirection: false },
  { chemin: '/calendrier/prevus/:planId', redirection: false },
  { chemin: '/calendrier/gels/:gelId', redirection: false },
  { chemin: '/taxonomy', redirection: false },
  { chemin: '/feedback', redirection: false },
  { chemin: '/programs', redirection: false },
  { chemin: '/guide', redirection: false },
  { chemin: '/admin', redirection: false },
  { chemin: '/config', redirection: false },
  { chemin: '/exigences', redirection: false },
  { chemin: '/org', redirection: true },
  { chemin: '/org/fiche', redirection: false },
  { chemin: '/org/derogations', redirection: false },
  { chemin: '/org/derogations/:waiverId', redirection: false },
  { chemin: '/org/exigences/:id', redirection: false },
  { chemin: '/org/finalites/:id', redirection: false },
  { chemin: '/org/delegations/:id', redirection: false },
  { chemin: '/org/projets', redirection: false },
  { chemin: '/org/suppressions/:id', redirection: false },
  { chemin: '/org/harnais', redirection: false },
  { chemin: '/org/technologies', redirection: false },
  { chemin: '/org/regles', redirection: false },
  { chemin: '/org/chemin-dore', redirection: false },
  { chemin: '/org/sante', redirection: false },
  { chemin: '/org/charte', redirection: false },
  { chemin: '/org/charte/:id', redirection: false },
  { chemin: '/org/gouvernance', redirection: false },
  { chemin: '/org/acces', redirection: false },
  { chemin: '/org/acces/revue', redirection: false },
  { chemin: '/org/utilisateurs', redirection: false },
  { chemin: '/org/utilisateurs/:userId', redirection: false },
  { chemin: '/org/invitations', redirection: false },
  { chemin: '/org/securite', redirection: false },
  { chemin: '/org/conformite', redirection: false },
  { chemin: '/programme', redirection: false },
  { chemin: '/programme/membres', redirection: false },
  { chemin: '/programme/projets', redirection: false },
  { chemin: '/programme/roadmap', redirection: false },
  { chemin: '/programme/versions', redirection: false },
  { chemin: '/programme/backlog', redirection: false },
  { chemin: '/programme/reglages', redirection: false },
  { chemin: '/programme/capacite', redirection: false },
  { chemin: '/programme/rapport', redirection: false },
  { chemin: '/programme/dependances', redirection: false },
  { chemin: '/programme/technologies', redirection: false },
  { chemin: '/programme/gouvernance', redirection: false },
  { chemin: '/plateforme', redirection: true },
  { chemin: '/plateforme/sante', redirection: false },
  { chemin: '/plateforme/organisations', redirection: false },
  { chemin: '/plateforme/organisations/:organisationId', redirection: false },
  { chemin: '/plateforme/suppressions/:id', redirection: false },
  { chemin: '/plateforme/ia', redirection: false },
  { chemin: '/plateforme/arret-ia', redirection: false },
  { chemin: '/plateforme/executants', redirection: false },
  { chemin: '/plateforme/decisions', redirection: false },
  { chemin: '/plateforme/file-gpu', redirection: false },
  { chemin: '/plateforme/journal', redirection: false },
  { chemin: '/plateforme/generation', redirection: false },
  { chemin: '/plateforme/harnais', redirection: false },
  { chemin: '/plateforme/continuite', redirection: false },
  { chemin: '/plateforme/acces-exceptionnels', redirection: false },
]
export const ROUTES_SOCLE_HORS_REDIRECTION = 131

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur)
}

function estDeclaration(valeur: unknown): valeur is DeclarationRoute {
  return estObjet(valeur) && typeof valeur.chemin === 'string' && typeof valeur.element === 'function'
    && (valeur.remplace === undefined || valeur.remplace === true)
}

function estRoutePublique(valeur: unknown): valeur is RoutePublique {
  return estObjet(valeur) && typeof valeur.chemin === 'string' && estComposant(valeur.ecran)
}

function estComposant<Props>(valeur: unknown): valeur is ComponentType<Props> {
  return typeof valeur === 'function' || (estObjet(valeur) && '$$typeof' in valeur
    && valeur.$$typeof !== Symbol.for('react.element')
    && valeur.$$typeof !== Symbol.for('react.transitional.element'))
}

/**
 * Motif tel que react-router le compare : seul le NOM d'un paramètre est effacé (`:id.json` → `:.json`, `:id?` → `:?` ;
 * le routeur garde le suffixe et le caractère optionnel), la casse est ignorée (`caseSensitive` vaut faux par défaut),
 * les barres multiples sont réduites et la barre finale retirée (sauf `/`).
 */
export function normaliserMotif(chemin: string): string {
  const motif = chemin.replace(/\/+/g, '/').replace(/\/+$/, '')
  return (motif || '/').split('/').map((segment) => segment.replace(/^:[\w-]+/, ':').toLowerCase()).join('/')
}

/** Développe chaque segment optionnel, statique ou paramétré, avec et sans ce segment. */
function developperMotif(chemin: string): string[][] {
  return normaliserMotif(chemin).split('/').filter(Boolean).reduce<string[][]>((variantes, segment) =>
    segment.endsWith('?')
      ? variantes.flatMap((variante) => [variante, [...variante, segment.slice(0, -1)]])
      : variantes.map((variante) => [...variante, segment]), [[]])
}

/** Recherche une URL commune ; un splat final accepte aussi un suffixe vide. */
export function motifsSeChevauchent(a: string, b: string): boolean {
  return developperMotif(a).some((gauche) => developperMotif(b).some((droite) => {
    const longueur = Math.max(gauche.length, droite.length)
    for (let index = 0; index < longueur; index += 1) {
      const segmentA = gauche[index]
      const segmentB = droite[index]
      if ((segmentA === '*' && index === gauche.length - 1)
        || (segmentB === '*' && index === droite.length - 1)) return true
      if (segmentA === undefined || segmentB === undefined) return false
      // Un segment paramètre (avec ou sans suffixe) peut valoir n'importe quel segment : comparaison prudente.
      if (!segmentA.startsWith(':') && !segmentB.startsWith(':') && segmentA !== segmentB) return false
    }
    return true
  }))
}

export function fragmentsRoutesDuDossier(modules: Record<string, unknown>): { fragments: FragmentRoutes[]; problemes: string[] } {
  const fragments: FragmentRoutes[] = []
  const problemes: string[] = []
  for (const fichier of Object.keys(modules).sort()) {
    const zone = /^\.\/([a-z0-9]+(?:-[a-z0-9]+)*)\.tsx$/.exec(fichier)?.[1]
    const module = modules[fichier]
    const fragment = estObjet(module) ? module.fragment : undefined
    if (!zone || zone === ZONE_SOCLE || zone === 'registre') {
      problemes.push(`ZONE_INVALIDE : ${zone ?? fichier} — ${fichier}`)
    } else if (!estObjet(fragment) || fragment.zone !== zone || !Array.isArray(fragment.routes)
      || !fragment.routes.every(estDeclaration)
      || ('publiques' in fragment && (!Array.isArray(fragment.publiques) || !fragment.publiques.every(estRoutePublique)))
      || ('coquille' in fragment && !estComposant(fragment.coquille))
      || ('connexion' in fragment && !estComposant(fragment.connexion))
      // Extension retirée en R3 (point d'amorçage du drapeau) : la déclarer est une erreur, jamais un oubli silencieux.
      || 'amorce' in fragment) {
      problemes.push(`FRAGMENT_INVALIDE : ${zone} — ${fichier}`)
    } else {
      fragments.push({
        zone, routes: fragment.routes,
        ...(Array.isArray(fragment.publiques) ? { publiques: fragment.publiques as RoutePublique[] } : {}),
        ...(estComposant<PropsCoquille>(fragment.coquille) ? { coquille: fragment.coquille } : {}),
        ...(estComposant<object>(fragment.connexion) ? { connexion: fragment.connexion } : {}),
      })
    }
  }
  fragments.sort((a, b) => a.zone < b.zone ? -1 : a.zone > b.zone ? 1 : 0)
  return { fragments, problemes }
}

/** Toutes les occurrences d'un doublon sont écartées, sans gagnant implicite. */
function analyserRegistre(socle: readonly string[], fragments: readonly FragmentRoutes[]) {
  const cheminsSocle = new Set(socle.map(normaliserMotif))
  const occurrences = new Map<string, Set<number>>()
  fragments.forEach((fragment, index) => {
    for (const { chemin } of fragment.routes) {
      const motif = normaliserMotif(chemin)
      const zones = occurrences.get(motif) ?? new Set<number>()
      zones.add(index)
      occurrences.set(motif, zones)
    }
  })
  const comptesPublics = new Map<string, number>()
  for (const fragment of fragments) {
    for (const { chemin } of fragment.publiques ?? []) {
      const motif = normaliserMotif(chemin)
      comptesPublics.set(motif, (comptesPublics.get(motif) ?? 0) + 1)
    }
  }
  const problemes: string[] = []
  const valides: DeclarationRoute[] = []
  const publiquesValides: RoutePublique[] = []
  const coquilles = fragments.filter((fragment) => fragment.coquille !== undefined)
  if (coquilles.length > 1) {
    for (const { zone } of coquilles) problemes.push(`COQUILLE_MULTIPLE : ${zone} — coquille`)
  }
  const connexions = fragments.filter((fragment) => fragment.connexion !== undefined)
  if (connexions.length > 1) {
    for (const { zone } of connexions) problemes.push(`CONNEXION_MULTIPLE : ${zone} — connexion`)
  }
  for (const fragment of fragments) {
    const comptes = new Map<string, number>()
    for (const { chemin } of fragment.routes) {
      const motif = normaliserMotif(chemin)
      comptes.set(motif, (comptes.get(motif) ?? 0) + 1)
    }
    for (const route of fragment.routes) {
      const { chemin, remplace } = route
      const motif = normaliserMotif(chemin)
      const codes: string[] = []
      if (!chemin.startsWith('/')) codes.push('CHEMIN_INVALIDE')
      if ((comptes.get(motif) ?? 0) > 1) codes.push('DOUBLON_DANS_FRAGMENT')
      if ((occurrences.get(motif)?.size ?? 0) > 1) codes.push('DOUBLON_FRAGMENTS')
      if (cheminsSocle.has(motif) && !remplace) codes.push('CONFLIT_SOCLE')
      if (!cheminsSocle.has(motif) && remplace) codes.push('REMPLACE_SANS_SOCLE')
      for (const code of codes) problemes.push(`${code} : ${fragment.zone} — ${chemin}`)
      if (codes.length === 0) valides.push(route)
    }
    for (const route of fragment.publiques ?? []) {
      const { chemin } = route
      const motif = normaliserMotif(chemin)
      const codes: string[] = []
      if (!chemin.startsWith('/') || /[?*]/.test(chemin)) codes.push('CHEMIN_INVALIDE')
      if ((comptesPublics.get(motif) ?? 0) > 1) codes.push('DOUBLON_PUBLIQUES')
      if (socle.some((prive) => motifsSeChevauchent(chemin, prive))
        || fragments.some(({ routes }) => routes.some((prive) => motifsSeChevauchent(chemin, prive.chemin)))) {
        codes.push('CONFLIT_PUBLIQUE')
      }
      for (const code of codes) problemes.push(`${code} : ${fragment.zone} — ${chemin}`)
      if (codes.length === 0) publiquesValides.push(route)
    }
  }
  return { problemes, valides, publiquesValides }
}

export function verifierRegistre(socle: readonly string[], fragments: readonly FragmentRoutes[]): string[] {
  return analyserRegistre(socle, fragments).problemes
}

const chargement = fragmentsRoutesDuDossier(import.meta.glob(['./*.tsx', '!./registre.tsx', '!./*.test.tsx'], { eager: true }))
/** Chemins du socle : routes statiques d'App.tsx puis entrées de navigation sans écran (`CHEMINS_SANS_ECRAN`). */
export const CHEMINS_SOCLE: readonly string[] = [...ROUTES_SOCLE.map(({ chemin }) => chemin), ...CHEMINS_SANS_ECRAN]
export const FRAGMENTS_ROUTES: readonly FragmentRoutes[] = chargement.fragments
export const PROBLEMES_CHARGEMENT_ROUTES: readonly string[] = [
  ...chargement.problemes, ...verifierRegistre(CHEMINS_SOCLE, FRAGMENTS_ROUTES),
]
if (PROBLEMES_CHARGEMENT_ROUTES.length > 0) console.error('[routes v10]', PROBLEMES_CHARGEMENT_ROUTES)

export function routesV10(ctx: ContexteRoutes, fragments: readonly FragmentRoutes[] = FRAGMENTS_ROUTES): JSX.Element[] {
  return analyserRegistre(CHEMINS_SOCLE, fragments).valides.map(({ chemin, element }) =>
    createElement(Route, { key: chemin, path: chemin, element: element(ctx) }))
}

/** Une ambiguïté de fournisseur ne choisit aucune coquille (`App.tsx` rend alors un état d'erreur nommé). */
export function coquilleV10(fragments: readonly FragmentRoutes[] = FRAGMENTS_ROUTES): ComponentType<PropsCoquille> | null {
  const coquilles = fragments.flatMap(({ coquille }) => coquille === undefined ? [] : [coquille])
  return coquilles.length === 1 ? coquilles[0] ?? null : null
}

/** Une ambiguïté de fournisseur ne choisit aucune connexion (`App.tsx` rend alors un état d'erreur nommé). */
export function connexionV10(fragments: readonly FragmentRoutes[] = FRAGMENTS_ROUTES): ComponentType | null {
  const connexions = fragments.flatMap(({ connexion }) => connexion === undefined ? [] : [connexion])
  return connexions.length === 1 ? connexions[0] ?? null : null
}

/** La validation précède la sélection : aucun élément en problème n'est appelé. */
export function routePubliqueV10(chemin: string, fragments: readonly FragmentRoutes[] = FRAGMENTS_ROUTES): JSX.Element | null {
  const publiques = analyserRegistre(CHEMINS_SOCLE, fragments).publiquesValides
  // matchRoutes classe les motifs comme <Routes> (`/statut/global` avant `/statut/:org`) : l'ordre de déclaration ne décide pas.
  if (matchRoutes(publiques.map(({ chemin: motif }) => ({ path: motif })), chemin) === null) return null
  return createElement(Routes, null, ...publiques.map((route) =>
    createElement(Route, { key: route.chemin, path: route.chemin, element: createElement(route.ecran) })))
}
